import type { Channel, Partner } from "@expert/shared-types";

import { getAppConfigSingleton } from "@expert/app-config";

import { type AllowedPartners, BlockedChannels, EnabledPartners } from "../types";

/**
 * Workspace partners that have no Expert University catalog of their own.
 * Profile, template, and later training calls use the mapped partner.
 */
const TRAINING_PARTNER_ALIASES: Partial<Record<Partner, AllowedPartners>> = {
    attnac: "att",
};

/** Resolves the partner Expert University should train as. `attnac` trains as `att`. */
export function getTrainingPartner(partner: Partner): Partner {
    return TRAINING_PARTNER_ALIASES[partner] ?? partner;
}

/**
 * Determines if Expert University is enabled for a partner on a given channel.
 * @param value Partner value (e.g., "verizon")
 * @param channel - Critical: some channels (e.g., "chat") may not support Expert University yet,
 *                  so a partner may be enabled in config, but really is unavailable if the channel is blocked.
 */
export function partnerEnabledForExpertUniversity(
    value: string,
    channel: Channel,
    isInPilot: boolean = false,
): value is AllowedPartners {
    const isChannelBlocked = BlockedChannels.includes(channel);
    // Aliased partners (attnac) follow the partner they train as.
    switch (TRAINING_PARTNER_ALIASES[value as Partner] ?? value) {
        case "verizon":
        case "att": {
            return !isChannelBlocked;
        }
        case "amazonrouter": {
            return !isChannelBlocked && isInPilot;
        }

        default: {
            return !isChannelBlocked && EnabledPartners.includes(value as AllowedPartners);
        }
    }
}

export function getFeatureOverrides(partner: Partner) {
    return getAppConfigSingleton().getPartnersConfig(partner).features;
}
